import { z } from 'zod';
import { baseMessageSchema } from './_base';
import { clientTypeSchema } from './shared';

export enum AuthMessageType {
  AUTH_REQUEST = 'AUTH_REQUEST',
  AUTH_UPGRADE = 'AUTH_UPGRADE',
  AUTH_SUCCESS = 'AUTH_SUCCESS',
  AUTH_UPGRADE_SUCCESS = 'AUTH_UPGRADE_SUCCESS',
  AUTH_ERROR = 'AUTH_ERROR',
  AUTH_SIGNIN_TOKEN = 'AUTH_SIGNIN_TOKEN',
  AUTH_SIGNIN_TOKEN_REPLY = 'AUTH_SIGNIN_TOKEN_REPLY',
}

// Payload schemas without the base message fields
const authRequestPayloadSchema = z.object({
  clientType: clientTypeSchema,
  tokenJwt: z.string().optional(),
  machineId: z.string().optional(),
  persistentId: z.string().optional(),
});

const authUpgradePayloadSchema = authRequestPayloadSchema;

const authSuccessPayloadSchema = z.object({
  userId: z.string(),
});

const authUpgradeSuccessPayloadSchema = z.object({
  userId: z.string(),
});

const authErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const authSigninTokenPayloadSchema = z.object({});

const authSigninTokenReplyPayloadSchema = z.object({
  token: z.string(),
  expiresIn: z.number(),
});

// Full message schemas including base fields
const authRequestSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_REQUEST),
  })
  .merge(authRequestPayloadSchema);

const authUpgradeSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_UPGRADE),
  })
  .merge(authUpgradePayloadSchema);

const authSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SUCCESS),
  })
  .merge(authSuccessPayloadSchema);

const authUpgradeSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_UPGRADE_SUCCESS),
  })
  .merge(authUpgradeSuccessPayloadSchema);

const authErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_ERROR),
  })
  .merge(authErrorPayloadSchema);

const authSigninTokenSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SIGNIN_TOKEN),
  })
  .merge(authSigninTokenPayloadSchema);

const authSigninTokenReplySchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SIGNIN_TOKEN_REPLY),
  })
  .merge(authSigninTokenReplyPayloadSchema);

export const authMessageSchema = z.discriminatedUnion('type', [
  authRequestSchema,
  authUpgradeSchema,
  authSuccessSchema,
  authUpgradeSuccessSchema,
  authErrorSchema,
  authSigninTokenSchema,
  authSigninTokenReplySchema,
]);

// Types for the payloads
export type AuthRequestPayload = z.infer<typeof authRequestPayloadSchema>;
export type AuthUpgradePayload = z.infer<typeof authUpgradePayloadSchema>;
export type AuthSuccessPayload = z.infer<typeof authSuccessPayloadSchema>;
export type AuthUpgradeSuccessPayload = z.infer<typeof authUpgradeSuccessPayloadSchema>;
export type AuthErrorPayload = z.infer<typeof authErrorPayloadSchema>;
export type AuthSigninTokenPayload = z.infer<typeof authSigninTokenPayloadSchema>;
export type AuthSigninTokenReplyPayload = z.infer<typeof authSigninTokenReplyPayloadSchema>;

// Type for the full messages
export type AuthMessage = z.infer<typeof authMessageSchema>;

type PayloadType<T extends AuthMessageType> = T extends AuthMessageType.AUTH_REQUEST
  ? AuthRequestPayload
  : T extends AuthMessageType.AUTH_UPGRADE
    ? AuthUpgradePayload
    : T extends AuthMessageType.AUTH_UPGRADE_SUCCESS
      ? AuthUpgradeSuccessPayload
      : T extends AuthMessageType.AUTH_SUCCESS
        ? AuthSuccessPayload
        : T extends AuthMessageType.AUTH_ERROR
          ? AuthErrorPayload
          : T extends AuthMessageType.AUTH_SIGNIN_TOKEN
            ? AuthSigninTokenPayload
            : T extends AuthMessageType.AUTH_SIGNIN_TOKEN_REPLY
              ? AuthSigninTokenReplyPayload
              : never;

export function generateAuthMessage<T extends AuthMessageType>(type: T, payload: PayloadType<T>): AuthMessage {
  const base = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case AuthMessageType.AUTH_REQUEST:
      return authRequestSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_UPGRADE:
      return authUpgradeSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SUCCESS:
      return authSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_UPGRADE_SUCCESS:
      return authUpgradeSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_ERROR:
      return authErrorSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SIGNIN_TOKEN:
      return authSigninTokenSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SIGNIN_TOKEN_REPLY:
      return authSigninTokenReplySchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
